# Selection Signals

This use case provides technical implementation details for sending selection signals to SEEK.
Selection signals represent the progression of candidates and positions through a recruitment process.
Feeding these back to SEEK helps to improve our search & recommendation capabilities,
and ultimately match candidates with the optimal positions on offer.

## Before you begin

Signal capture is supported for partners that have implemented [Job Posting] and/or [Optimised Apply].
It is not currently available for recruitment software providers that post SEEK job ads via middleware and solely support [linking out] to an external apply form.

By building Selection Signals,
you agree to the additional terms specified in Section K of our [SEEK API Terms of Use].

GraphQL operations for Selection Signals require a [partner token](/auth/partner-tokens).

## Key concepts

Selection Signals refers to its key concepts using the following terminology:

- A **position** is an opening at an organization that may be advertised through different channels

  Your software may refer to this concept as an employment opportunity, job order, job requisition, job vacancy, or position opening.

- A **candidate** is an individual who could fill the position

  Your software may refer to this concept as an applicant, jobseeker, or talent.

- An **application** is a candidate’s expression of interest for a given position

A signal must be sent to SEEK whenever a relevant application or position moves in the recruitment process in your software:

- A `PositionStatusUpdate` signal upon changes to the status of a position, if it was advertised on SEEK
- An `ApplicationComplete` signal upon submission of an external apply form, [if the candidate used SEEK to apply for the position]
- An `ApplicationStatusUpdate` signal upon changes to the status of an application, [if the candidate used SEEK to apply for the position]

## Data flow

```mermaid Selection Signals
---
config:
  layout: elk
---
flowchart TD
jobAd(SEEK job ad)

subgraph Partner
  Application(Application)
  ApplicationUpdate{Status update 1..N}
  Position(Position)
  PositionUpdate{Status update 1..N}
  linkOutApply[External apply form]
end

SeekApi[SEEK API]
ApplyMethod{Apply method}
SeekApply[SEEK’s Apply Form]

Position --> linkOutApply
ApplyMethod -- Apply with SEEK --> linkOutApply ---> Application

jobAd <-. (linked) .-> Position

jobAd --> ApplyMethod
ApplyMethod -- Optimised Apply --> SeekApply --> Application

Application --> ApplicationUpdate -. ApplicationStatusUpdate .-> SeekApi
linkOutApply -. ApplicationComplete .-> SeekApi
Position --> PositionUpdate -. PositionStatusUpdate .-> SeekApi
```

## Next steps

| Section               | Description                                       |
| :-------------------- | :------------------------------------------------ |
| [Data structure]      | Walk through data structure & mapping concerns    |
| [Application signals] | Break down scenarios for application signals      |
| [Position signals]    | Break down scenarios for position signals         |
| [Sending signals]     | Provide implementation guidance on signal capture |

[Application signals]: /use-cases/selection-signals/application-signals
[if the candidate used SEEK to apply for the position]: /use-cases/selection-signals/application-signals
[Data structure]: /use-cases/selection-signals/data-structure
[Job Posting]: /use-cases/job-posting
[linking out]: /use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form
[Optimised Apply]: /use-cases/application-export
[Position signals]: /use-cases/selection-signals/position-signals
[SEEK API Terms of Use]: https://au.employer.seek.com/partners/terms-of-use
[Sending signals]: /use-cases/selection-signals/sending-signals
